import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Settings as SettingsIcon, CheckCircle, XCircle, AlertCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const [emailStatus, setEmailStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSMTPForm, setShowSMTPForm] = useState(false);
  const [smtpConfig, setSmtpConfig] = useState({
    smtp_host: '',
    smtp_port: '465',
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    sender_email: '',
    sender_name: 'Kabin Memuru Adayımız'
  });

  useEffect(() => {
    fetchEmailStatus();
    fetchSMTPSettings();
  }, []);

  const fetchEmailStatus = async () => {
    try {
      const response = await axios.get(`${API}/email/status`);
      setEmailStatus(response.data);
    } catch (error) {
      toast.error('E-posta servis durumu alınamadı');
    } finally {
      setLoading(false);
    }
  };

  const fetchSMTPSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/smtp`);
      setSmtpConfig({
        smtp_host: response.data.smtp_host || '',
        smtp_port: response.data.smtp_port || '465',
        smtp_username: response.data.smtp_username || '',
        smtp_password: '',  // Don't populate password
        smtp_use_tls: response.data.smtp_use_tls !== false,
        sender_email: response.data.sender_email || '',
        sender_name: response.data.sender_name || 'Kabin Memuru Adayımız'
      });
    } catch (error) {
      console.error('SMTP ayarları alınamadı:', error);
    }
  };

  const handleSMTPChange = (field, value) => {
    setSmtpConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSMTP = async () => {
    // Validation
    if (!smtpConfig.smtp_host || !smtpConfig.smtp_username || !smtpConfig.sender_email) {
      toast.error('Lütfen tüm gerekli alanları doldurun');
      return;
    }

    if (!smtpConfig.smtp_password && !showSMTPForm) {
      toast.error('Lütfen şifre girin');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        smtp_host: smtpConfig.smtp_host,
        smtp_port: parseInt(smtpConfig.smtp_port),
        smtp_username: smtpConfig.smtp_username,
        smtp_password: smtpConfig.smtp_password,
        smtp_use_tls: smtpConfig.smtp_use_tls,
        sender_email: smtpConfig.sender_email,
        sender_name: smtpConfig.sender_name
      };

      await axios.post(`${API}/settings/smtp`, payload);
      toast.success('SMTP ayarları başarıyla kaydedildi');
      setShowSMTPForm(false);
      fetchEmailStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP ayarları kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/settings/smtp/test`);
      toast.success('SMTP bağlantısı başarılı!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP bağlantı testi başarısız');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight">Ayarlar</h1>
        <p className="text-lg text-[#64748b] mt-2">E-posta gönderim ayarlarını yapılandırın</p>
      </div>

      {/* SMTP Configuration */}
      <div className="bg-white rounded-xl border border-[#e2e8f0] p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Mail size={24} className="text-[#6366f1]" />
            <h2 className="text-2xl font-bold text-[#0f172a]">SMTP Yapılandırması</h2>
          </div>
          {!showSMTPForm && (
            <Button
              onClick={() => setShowSMTPForm(true)}
              className="bg-[#6366f1] hover:bg-[#4f46e5]"
            >
              Ayarları Düzenle
            </Button>
          )}
        </div>

        {showSMTPForm ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* SMTP Host */}
              <div>
                <label className="block text-sm font-medium text-[#0f172a] mb-2">
                  SMTP Sunucusu <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={smtpConfig.smtp_host}
                  onChange={(e) => handleSMTPChange('smtp_host', e.target.value)}
                  placeholder="mail.sirketadi.com"
                  className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                />
              </div>

              {/* SMTP Port */}
              <div>
                <label className="block text-sm font-medium text-[#0f172a] mb-2">
                  Port <span className="text-red-500">*</span>
                </label>
                <select
                  value={smtpConfig.smtp_port}
                  onChange={(e) => {
                    const port = e.target.value;
                    handleSMTPChange('smtp_port', port);
                    handleSMTPChange('smtp_use_tls', port === '587');
                  }}
                  className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                >
                  <option value="465">465 (SSL)</option>
                  <option value="587">587 (TLS)</option>
                  <option value="25">25 (Plain)</option>
                </select>
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-[#0f172a] mb-2">
                  Kullanıcı Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={smtpConfig.smtp_username}
                  onChange={(e) => handleSMTPChange('smtp_username', e.target.value)}
                  placeholder="noreply@sirketadi.com"
                  className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-[#0f172a] mb-2">
                  Şifre <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={smtpConfig.smtp_password}
                  onChange={(e) => handleSMTPChange('smtp_password', e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                />
              </div>

              {/* Sender Email */}
              <div>
                <label className="block text-sm font-medium text-[#0f172a] mb-2">
                  Gönderen E-posta <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={smtpConfig.sender_email}
                  onChange={(e) => handleSMTPChange('sender_email', e.target.value)}
                  placeholder="noreply@sirketadi.com"
                  className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                />
              </div>

              {/* Sender Name */}
              <div>
                <label className="block text-sm font-medium text-[#0f172a] mb-2">
                  Gönderen Adı
                </label>
                <input
                  type="text"
                  value={smtpConfig.sender_name}
                  onChange={(e) => handleSMTPChange('sender_name', e.target.value)}
                  placeholder="Kabin Memuru Adayımız"
                  className="w-full px-4 py-2 border border-[#e2e8f0] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6366f1]"
                />
              </div>
            </div>

            {/* TLS Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>💡 Not:</strong> Port 465 SSL kullanır, Port 587 TLS kullanır. Sunucunuz hangisini destekliyorsa onu seçin.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleSaveSMTP}
                disabled={saving}
                className="bg-[#6366f1] hover:bg-[#4f46e5] flex-1"
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
              <Button
                onClick={handleTestConnection}
                disabled={testing || !emailStatus?.configured}
                variant="outline"
                className="flex-1"
              >
                {testing ? 'Test Ediliyor...' : 'Bağlantıyı Test Et'}
              </Button>
              <Button
                onClick={() => setShowSMTPForm(false)}
                variant="outline"
              >
                İptal
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Current SMTP Status */}
            <div className="flex items-start gap-4 p-6 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]">
              <div className="mt-1">
                {emailStatus?.configured && emailStatus?.service === 'SMTP' ? (
                  <CheckCircle size={24} className="text-[#10b981]" />
                ) : (
                  <XCircle size={24} className="text-[#ef4444]" />
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-[#0f172a] mb-2">
                  {emailStatus?.configured && emailStatus?.service === 'SMTP' 
                    ? 'SMTP Yapılandırıldı' 
                    : 'SMTP Yapılandırılmadı'}
                </h3>
                <p className="text-sm text-[#64748b] mb-4">
                  {emailStatus?.configured && emailStatus?.service === 'SMTP'
                    ? 'SMTP sunucunuz yapılandırıldı ve e-posta göndermeye hazır.'
                    : 'E-posta göndermek için SMTP sunucu ayarlarını yapılandırın.'}
                </p>

                {emailStatus?.configured && emailStatus?.service === 'SMTP' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-[#64748b] mb-1">Sunucu</p>
                      <p className="text-sm font-mono text-[#0f172a] bg-white px-3 py-2 rounded border border-[#e2e8f0]">
                        {smtpConfig.smtp_host || 'Yapılandırılmadı'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#64748b] mb-1">Gönderen</p>
                      <p className="text-sm font-mono text-[#0f172a] bg-white px-3 py-2 rounded border border-[#e2e8f0]">
                        {smtpConfig.sender_name} &lt;{smtpConfig.sender_email}&gt;
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SMTP Avantajları */}
      <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
        <h3 className="text-lg font-bold text-[#0f172a] mb-4">SMTP ile E-posta Gönderimi</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-[#f8fafc] rounded-lg">
            <h4 className="font-bold text-[#0f172a] mb-2">🔒 Tam Kontrol</h4>
            <p className="text-sm text-[#64748b]">Kendi sunucunuzu kullanarak tam kontrol sahibi olun</p>
          </div>
          <div className="p-4 bg-[#f8fafc] rounded-lg">
            <h4 className="font-bold text-[#0f172a] mb-2">💼 Profesyonel</h4>
            <p className="text-sm text-[#64748b]">Kendi domain'inizden e-posta gönderin</p>
          </div>
          <div className="p-4 bg-[#f8fafc] rounded-lg">
            <h4 className="font-bold text-[#0f172a] mb-2">🔧 Esnek</h4>
            <p className="text-sm text-[#64748b]">Herhangi bir SMTP sunucusu ile çalışır</p>
          </div>
          <div className="p-4 bg-[#f8fafc] rounded-lg">
            <h4 className="font-bold text-[#0f172a] mb-2">📊 Güvenilir</h4>
            <p className="text-sm text-[#64748b]">Endüstri standardı e-posta protokolü</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;